import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, ShieldAlert, Share2, Archive, Lock } from 'lucide-react';
import { SmoothScroll } from '../components/SmoothScroll';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const sections = [
  {
    Icon: Globe,
    title: 'Domain Ownership',
    body: 'The otresilience.com domain and this website are owned and operated by OT Resilience. All content, branding, trademarks, logos and materials appearing on this site are the exclusive property of OT Resilience and may not be reproduced, republished or redistributed without prior written consent.'
  },
  {
    Icon: ShieldAlert,
    title: 'Impersonation & Misuse',
    body: 'We do not authorize any third party to use the OT Resilience name, logo, or branding to solicit business, request payment, or represent themselves as us. If you receive an unsolicited communication claiming to be from OT Resilience — particularly one requesting sensitive information, credentials, or payment — please verify directly via info@otresilience.com before responding. We investigate and act on all reported impersonation.'
  },
  {
    Icon: Share2,
    title: 'Data Sharing',
    body: 'Any information you submit through our contact forms (name, email, company, message) is used solely to respond to your enquiry. We do not sell, rent, trade, or share this information with third parties for marketing purposes. Data may only be disclosed when required by law or with your explicit consent.'
  },
  {
    Icon: Archive,
    title: 'Data Retention',
    body: 'We retain contact submissions only for as long as necessary to respond to your enquiry and maintain a reasonable record of the engagement. You may request deletion of your data at any time by emailing info@otresilience.com and we will action the request within 30 days.'
  },
  {
    Icon: Lock,
    title: 'Data Security',
    body: 'We apply industry-standard technical and organisational measures to protect the information you share with us — including encrypted transport (HTTPS), access controls, and least-privilege handling internally. As an OT cybersecurity firm, we hold our own systems to the same standards we recommend to our clients.'
  }
];

const PrivacyPage = () => {
  return (
    <SmoothScroll>
      <div className="relative bg-slate-50">
        <Header />

        {/* Hero band */}
        <section className="relative pt-40 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.12 }}
            transition={{ duration: 1.5 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-amber-500 rounded-full blur-[150px]"
          />

          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <Link
                to="/"
                data-testid="privacy-back-to-home"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors duration-300 group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
                <span className="text-sm tracking-wide uppercase">Back to Home</span>
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-amber-400 text-sm tracking-[0.3em] uppercase mb-6"
            >
              Legal
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold leading-tight mb-6"
            >
              <span className="bg-gradient-to-r from-white via-amber-50 to-amber-400 bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-slate-300 max-w-2xl font-light"
            >
              A plain-language summary of how we handle your information, our domain, and our security posture.
            </motion.p>
          </div>
        </section>

        {/* Sections */}
        <section className="py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-6 space-y-6">
            {sections.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="p-8 bg-white border border-slate-200 rounded-2xl hover:border-amber-400 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-950" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-950 mb-3">{title}</h2>
                    <p className="text-slate-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-sm text-slate-500 text-center pt-8"
            >
              Questions about this policy? Reach us at{' '}
              <a href="mailto:info@otresilience.com" className="text-amber-600 hover:text-amber-700 transition-colors">
                info@otresilience.com
              </a>
              .
            </motion.p>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
};

export default PrivacyPage;
